import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { Row } from '../../types';
import type { Act, Scene } from '../show-structure/types';

export interface RunSheetSlice {
    rows: Row[];
    selection: {
        activeSceneId: string | null;
        selectedRowIds: Set<string>;
    };
    runSheetActions: {
        setRows: (rows: Row[]) => void;
        addRow: (sceneId: string, afterRowId?: string) => string;
        updateRow: (rowId: string, updates: Partial<Row>) => void;
        deleteRow: (rowId: string) => void;
        moveRow: (rowId: string, targetSceneId: string, targetIndex: number) => void;
        duplicateRow: (rowId: string) => void;
        setActiveSceneId: (sceneId: string | null) => void;
        
        // Legacy structure actions - delegate to showStructureActions
        // These are kept for backward compatibility during migration
        setStructure: (acts: Act[], scenes: Scene[]) => void;
        addAct: (name: string) => void;
        addScene: (actId: string, name: string) => void;
        updateAct: (actId: string, updates: Partial<Act>) => void;
        deleteAct: (actId: string) => void;
        updateScene: (sceneId: string, updates: Partial<Scene>) => void;
        deleteScene: (sceneId: string) => void;
        moveScene: (actId: string, sceneId: string, newIndex: number) => void;
        
        // Project scoping
        clearRows: () => void;
    };
}

export const createRunSheetSlice: StateCreator<
    AppState,
    [["zustand/immer", never]],
    [],
    RunSheetSlice
> = (set, get) => ({
    rows: [],
    selection: {
        activeSceneId: null,
        selectedRowIds: new Set()
    },
    runSheetActions: {
        setRows: (rows) => set((state) => {
            state.rows = rows;
        }),
        
        addRow: (sceneId, afterRowId) => {
            const newId = crypto.randomUUID();
            set((state) => {
                // Find insertion index
                let index = state.rows.filter(r => r.sceneId === sceneId).length;
                if (afterRowId) {
                    const afterIndex = state.rows.findIndex(r => r.id === afterRowId);
                    if (afterIndex !== -1) {
                        // Insert after, within same scene rows
                        const sceneRows = state.rows.filter(r => r.sceneId === sceneId);
                        const afterRow = sceneRows.findIndex(r => r.id === afterRowId);
                        if (afterRow !== -1) index = afterRow + 1;
                    }
                }
                
                const newRow: Row = {
                    id: newId,
                    sceneId,
                    number: String(index + 1),
                    moment: '',
                    type: 'NULL',
                    isDeckCue: false,
                    item: '',
                    location: '',
                    who: '',
                    notes: '',
                    deleted: false
                };
                
                // Find global insertion index
                const sceneRowsGlobal = state.rows
                    .map((r, i) => ({ ...r, globalIndex: i }))
                    .filter(r => r.sceneId === sceneId);
                
                if (sceneRowsGlobal.length === 0) {
                    state.rows.push(newRow);
                } else if (index >= sceneRowsGlobal.length) {
                    const lastSceneRow = sceneRowsGlobal[sceneRowsGlobal.length - 1]!;
                    state.rows.splice(lastSceneRow.globalIndex + 1, 0, newRow);
                } else {
                    const targetRow = sceneRowsGlobal[index]!;
                    state.rows.splice(targetRow.globalIndex, 0, newRow);
                }
            });
            return newId;
        },
        
        updateRow: (rowId, updates) => set((state) => {
            const row = state.rows.find(r => r.id === rowId);
            if (row) Object.assign(row, updates);
        }),
        
        deleteRow: (rowId) => set((state) => {
            const index = state.rows.findIndex(r => r.id === rowId);
            if (index !== -1) state.rows.splice(index, 1);
        }),
        
        moveRow: (rowId, targetSceneId, targetIndex) => set((state) => {
            const rowIndex = state.rows.findIndex(r => r.id === rowId);
            if (rowIndex === -1) return;
            
            const row = state.rows[rowIndex]!;
            
            // Remove from old position
            state.rows.splice(rowIndex, 1);
            
            // Update sceneId
            row.sceneId = targetSceneId;
            
            // Find target scene rows
            const sceneRowIndices = state.rows
                .map((r, i) => ({ id: r.id, index: i }))
                .filter((_, i) => state.rows[i]!.sceneId === targetSceneId);
            
            if (sceneRowIndices.length === 0 || targetIndex >= sceneRowIndices.length) {
                // Append to end of scene or rows
                const lastSceneRowGlobal = sceneRowIndices.length > 0 
                    ? sceneRowIndices[sceneRowIndices.length - 1]!.index 
                    : state.rows.length - 1;
                state.rows.splice(lastSceneRowGlobal + 1, 0, row);
            } else {
                const targetGlobalIdx = sceneRowIndices[targetIndex]!.index;
                state.rows.splice(targetGlobalIdx, 0, row);
            }
        }),
        
        duplicateRow: (rowId) => set((state) => {
            const row = state.rows.find(r => r.id === rowId);
            if (row) {
                const newRow: Row = {
                    ...row,
                    id: crypto.randomUUID(),
                };
                
                // Insert after the current row
                const idx = state.rows.indexOf(row);
                if (idx !== -1) {
                    state.rows.splice(idx + 1, 0, newRow);
                } else {
                    state.rows.push(newRow);
                }
            }
        }),
        
        setActiveSceneId: (sceneId) => set((state) => {
            state.selection.activeSceneId = sceneId;
        }),
        
        // Legacy delegation methods - forward to showStructureActions
        setStructure: (acts, scenes) => {
            get().showStructureActions.setStructure(acts, scenes);
        },
        addAct: (name) => {
            get().showStructureActions.addAct(name);
        },
        addScene: (actId, name) => {
            get().showStructureActions.addScene(actId, name);
        },
        updateAct: (actId, updates) => {
            get().showStructureActions.updateAct(actId, updates);
        },
        deleteAct: (actId) => {
            get().showStructureActions.deleteAct(actId);
            // Also cleanup rows for deleted scenes
            set((state) => {
                // This assumes showStructure is part of AppState and accessible via `state`
                // If showStructure is not directly on `state` but only via `get().showStructure`,
                // this logic would need adjustment. Assuming it's on `state` for now.
                const deletedScenes = get().showStructure.scenes
                    .filter(s => !get().showStructure.acts.some(a => a.sceneIds.includes(s.id)));
                for (const scene of deletedScenes) {
                    state.rows = state.rows.filter(r => r.sceneId !== scene.id);
                }
            });
        },
        updateScene: (sceneId, updates) => {
            get().showStructureActions.updateScene(sceneId, updates);
        },
        deleteScene: (sceneId) => {
            get().showStructureActions.deleteScene(sceneId);
            // Cleanup rows for deleted scene
            set((state) => {
                state.rows = state.rows.filter(r => r.sceneId !== sceneId);
            });
        },
        moveScene: (actId, sceneId, newIndex) => {
            get().showStructureActions.moveScene(actId, sceneId, newIndex);
        },
        
        clearRows: () => set(state => {
            state.rows = [];
            state.selection.activeSceneId = null;
            state.selection.selectedRowIds = new Set();
        })
    }
});








